import { PAGE_SIZE } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/constants'
import { useCallback, useMemo } from 'react'

interface UseDocumentCardPaginationParams<T> {
  filteredDocuments: T[]
  totalCount: number
  filteredCount: number
  hasActiveFilters: boolean
  activeIndex: number
  onActiveIndexChange: (index: number) => void
}

export interface DocumentCardPagination<T> {
  currentPage: number
  displayCount: number
  pageDocuments: T[]
  pageActiveIndex: number
  pageEnd: number
  pageStart: number
  totalPages: number
  handlePageChange: (page: number) => void
}

export function useDocumentCardPagination<T>({
  filteredDocuments,
  totalCount,
  filteredCount,
  hasActiveFilters,
  activeIndex,
  onActiveIndexChange,
}: UseDocumentCardPaginationParams<T>): DocumentCardPagination<T> {
  const displayCount = hasActiveFilters ? filteredCount : totalCount
  const clampedActiveIndex =
    filteredDocuments.length === 0
      ? 0
      : Math.min(Math.max(activeIndex, 0), filteredDocuments.length - 1)
  const currentPage = Math.floor(clampedActiveIndex / PAGE_SIZE)
  const pageStart = currentPage * PAGE_SIZE
  const pageEnd = Math.min(pageStart + PAGE_SIZE, filteredDocuments.length)
  const pageDocuments = useMemo(
    () => filteredDocuments.slice(pageStart, pageEnd),
    [filteredDocuments, pageStart, pageEnd]
  )
  const totalPages = Math.ceil(filteredDocuments.length / PAGE_SIZE)
  const pageActiveIndex = clampedActiveIndex - pageStart

  const handlePageChange = useCallback(
    (page: number) => {
      onActiveIndexChange(page * PAGE_SIZE)
    },
    [onActiveIndexChange]
  )

  return {
    currentPage,
    displayCount,
    pageDocuments,
    pageActiveIndex,
    pageEnd,
    pageStart,
    totalPages,
    handlePageChange,
  }
}
